import i18n from '@lib/i18n.config'
import { I18nextProvider } from 'react-i18next'
import { Tenants } from '@app/tenants'
import { Layout } from '@components/Layout'
import { ErrorBoundary } from '@components/ErrorBoundary'
import { SupabaseProvider, useSupabase } from '@packages/supabase'
import { SessionProvider } from '@lib/contexts/sessionContext'
import { MantineProvider, Stack } from '@mantine/core'
import { ModalsProvider } from '@mantine/modals'

// import route components
import { Dashboard } from '@app/dashboard'
import { SignIn } from '@app/signin'
import { PlanManagement } from '@app/stripe'
import { SystemNotifications } from '@app/notifications'
import { Prompts } from '@app/prompts'
import { Schemas } from '@app/schemas'
import { DemoContent } from '@app/demo-content'
import { PreferencesAdministration } from '@app/preferences'
import { FeaturesAdministration } from '@app/features'
import { Security } from '@app/security'

import { useEffect } from 'react'
import {
  BrowserRouter,
  Navigate,
  Route,
  Routes,
  useNavigate
} from 'react-router'

import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
dayjs.extend(utc)

import { mantineTheme } from '@packages/theme'
import './index.css'

const supabaseConfig = {
  apiURL: import.meta.env.VITE_SUPABASE_URL,
  anonKey: import.meta.env.VITE_SUPABASE_ANON_KEY,
  projectRef: import.meta.env.VITE_SUPABASE_PROJECT_ID
}

const AuthCallback = () => {
  const navigate = useNavigate()
  const { supabase } = useSupabase()

  useEffect(() => {
    const finishAuth = async () => {
      const hashParams = window.location.hash.substring(1)
      const queryParams = window.location.search.substring(1)
      const params = new URLSearchParams(hashParams || queryParams)
      const code = params.get('code')
      const access_token = params.get('access_token')
      const refresh_token = params.get('refresh_token')

      try {
        if (code) {
          const { error } = await supabase.auth.exchangeCodeForSession(
            window.location.href
          )
          if (error) {
            console.error('auth callback code exchange failed:', error)
          }
        } else if (access_token && refresh_token) {
          const { error } = await supabase.auth.setSession({
            access_token,
            refresh_token
          })
          if (error) {
            console.error('auth callback session set failed:', error)
          }
        }
      } finally {
        navigate('/', { replace: true })
      }
    }

    void finishAuth()
  }, [navigate, supabase.auth])

  return <Stack>Authenticating...</Stack>
}

const MainRoutes = () => {
  const { session } = useSupabase()
  return (
    <Routes>
      <Route path="/auth-callback" element={<AuthCallback />} />

      {!session && (
        <>
          <Route path="/" element={<SignIn />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </>
      )}

      {session && (
        <Route element={<Layout />}>
          <Route path="/" element={<Dashboard />} />
          <Route path="/tenants/*" element={<Tenants />} />
          <Route path="/notifications" element={<SystemNotifications />} />
          <Route path="/prompts" element={<Prompts />} />
          <Route path="/schemas" element={<Schemas />} />
          <Route path="/demo-content" element={<DemoContent />} />
          <Route path="/preferences" element={<PreferencesAdministration />} />
          <Route path="/features" element={<FeaturesAdministration />} />
          <Route path="/security" element={<Security />} />
          <Route path="/plans" element={<PlanManagement />} />
          <Route path="/stripe" element={<Navigate to="/plans" replace />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Route>
      )}
    </Routes>
  )
}

export const App = () => {
  return (
    <I18nextProvider i18n={i18n}>
      <BrowserRouter>
        <MantineProvider theme={mantineTheme}>
          <ModalsProvider>
            <SupabaseProvider config={supabaseConfig}>
              <SessionProvider>
                <ErrorBoundary title="Admin app crashed">
                  <MainRoutes />
                </ErrorBoundary>
              </SessionProvider>
            </SupabaseProvider>
          </ModalsProvider>
        </MantineProvider>
      </BrowserRouter>
    </I18nextProvider>
  )
}
